import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';

interface HowItWorksProps {
  onStartQuest?: () => void;
}

export function HowItWorks({ onStartQuest }: HowItWorksProps) {
  const navigate = useNavigate();

  const handleStartQuest = () => {
    if (onStartQuest) {
      onStartQuest();
    }
    navigate('/campaigns');
  };
  return (
    <>
      <Header />
      <div className="how-it-works-root">
        {/* Hero Section */}
        <section className="how-it-works-hero">
          <div className="how-it-works-hero-content">
            <h1 className="how-it-works-title">
              How it Works
            </h1>
            <p className="how-it-works-subtitle">
              Welcome to <strong>Learn by Campaign</strong>!
            </p>
            <p className="how-it-works-intro">
              A place where difficult ideas become things you can actually experience.
            </p>
            <p className="how-it-works-intro">
              You enter a story, make choices, ask questions, get things wrong, and slowly build an intuition for concepts that can feel abstract on a page.
            </p>
          </div>
        </section>

        {/* The Problem Section */}
        <section className="how-it-works-section">
          <div className="how-it-works-container">
            <h2>The Problem with Abstract Ideas</h2>
            <p>
              A lot of things are hard to learn for a simple reason: you never really get to see what they mean.
            </p>
            <blockquote className="how-it-works-quote">
              <p>
                You read a definition. Maybe it makes sense.<br/>
                Then you move on, and a week later it's mostly gone.
              </p>
            </blockquote>
            <p>
              Learn by Campaign tries a different route.
            </p>
            <p>
              Instead of starting from the explanation, it gives you a situation where the idea matters.
            </p>
          </div>
        </section>

        {/* The Story Section */}
        <section className="how-it-works-section how-it-works-story">
          <div className="how-it-works-container">
            <h2>An Example: The Poisoned Goblet</h2>

            <div className="story-passage">
              <p className="story-setting">
                <em>You enter a crypt.</em>
              </p>
              <p>
                There are two goblets on a stone table. One may be poisoned.
              </p>
              <p>
                One has a faint violet stain.
              </p>
            </div>

            <div className="story-dialogue">
              <div className="dialogue-line">
                <div className="dialogue-speaker">You:</div>
                <div className="dialogue-text">That one's poisoned.</div>
              </div>

              <div className="dialogue-line">
                <div className="dialogue-speaker">Seren:</div>
                <div className="dialogue-text">
                  "Maybe. The stain shows up often with poison. But sometimes it's there on safe goblets too."
                </div>
              </div>

              <div className="dialogue-line">
                <div className="dialogue-speaker">You:</div>
                <div className="dialogue-text">So it's not enough.</div>
              </div>

              <div className="dialogue-line">
                <div className="dialogue-speaker">Seren:</div>
                <div className="dialogue-text">
                  "No. And poison is pretty rare here."
                </div>
              </div>

              <div className="dialogue-line">
                <div className="dialogue-speaker">You:</div>
                <div className="dialogue-text">Right. So I can't ignore what I knew before seeing the stain.</div>
              </div>

              <div className="dialogue-line">
                <div className="dialogue-speaker">The Abbot:</div>
                <div className="dialogue-text">
                  "Bayes would approve."
                </div>
              </div>

              <div className="dialogue-line">
                <div className="dialogue-speaker">You:</div>
                <div className="dialogue-text">Who?</div>
              </div>

              <div className="dialogue-line">
                <div className="dialogue-speaker">The Abbot:</div>
                <div className="dialogue-text">
                  "Doesn't matter. Just remember this: a clue changes what you believe. It doesn't replace everything you knew before."
                </div>
              </div>
            </div>

            <div className="story-passage">
              <p className="story-action">
                <em>Seren pushes the goblet toward you.</em>
              </p>
              <p className="story-question">
                <strong>Seren:</strong> "So?"
              </p>
            </div>
          </div>
        </section>

        {/* The Insight Section */}
        <section className="how-it-works-section">
          <div className="how-it-works-container">
            <h2>Why This Works</h2>
            <p>
              That's the idea.
            </p>
            <p>
              You meet the concept inside a problem first.
            </p>
            <p>
              You can ask questions, make a bad guess, test something, change your mind. The story keeps moving, but the subject underneath it is real.
            </p>
            <blockquote className="how-it-works-quote">
              <p>
                Later, the formal name can come in.<br/>
                The formula too, if it matters.
              </p>
              <p>
                By then, you already have something to attach it to.
              </p>
            </blockquote>
            <p>
              A room.<br/>
              A choice.<br/>
              A mistake you almost made.
            </p>
            <p>
              That tends to stick better than another paragraph in a textbook.
            </p>
          </div>
        </section>

        {/* For Whom Section */}
        <section className="how-it-works-section">
          <div className="how-it-works-container">
            <h2>Who Is This For?</h2>
            <p>
              Learn by Campaign is for subjects that are within reach, but still feel slippery when they stay abstract.
            </p>
            <p>
              You don't need them simplified into nothing.
            </p>
            <p>
              You need a chance to experience what they mean.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="how-it-works-section how-it-works-cta-section">
          <div className="how-it-works-container">
            <h2>Ready to Begin?</h2>
            <p className="how-it-works-cta-text">
              Create your first campaign and start learning through experience.
            </p>
            <button className="cta-primary" onClick={handleStartQuest}>
              Start a Quest
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
